<template>
  <div class="calendario-wrapper">
    <FullCalendar ref="calRef" :options="calOptions" />
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import { useCalendarioStore } from "@/store/calendarioStore";

const store = useCalendarioStore();
const calRef = ref(null);

const emit = defineEmits(["clickDia", "clickEvento"]);

// ── Opciones FullCalendar ────────────────────────────────────
const calOptions = computed(() => ({
  plugins: [dayGridPlugin, interactionPlugin],
  initialView: "dayGridMonth",
  locale: esLocale,
  headerToolbar: false,
  height: "calc(100vh - 165px)",
  contentHeight: "auto",
  selectable: true,
  editable: false,
  events: store.eventosFullCalendar,

  // Click en día vacío → crear evento
  // CORRECCIÓN: se filtra por fc-day-other para ignorar días de meses adyacentes.
  // La validación de estatus se hace en CalendarioMtto.vue (onClickDia).
  dateClick(info) {
    // fc-day-other = día que pertenece a un mes distinto al que se muestra
    if (info.dayEl.classList.contains("fc-day-other")) return;
    emit("clickDia", info.dateStr);
  },

  // Click en evento → ver detalle (siempre, sin importar estatus)
  eventClick(info) {
    emit("clickEvento", info.event.extendedProps);
  },

  // Estilo de cada evento
  eventDidMount(info) {
    info.el.style.borderRadius = "5px";
    info.el.style.fontSize = "0.68rem";
    info.el.style.fontWeight = "700";
    info.el.style.padding = "1px 5px";
    info.el.style.cursor = "pointer";
    info.el.title = `${info.event.extendedProps.maquina_nombre} · ${info.event.extendedProps.rutina_nombre}`;
  },

  // Cargar mes al navegar (controlado desde el store)
  datesSet(info) {
    const fecha = info.view.currentStart;
    const mes = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, "0")}`;
    if (mes !== store.mesActual) store.cargarMes(mes);
  },
}));

// ── Sincronizar navegación externa con FullCalendar ───────────
watch(
  () => store.mesActual,
  (nuevoMes) => {
    const api = calRef.value?.getApi();
    if (!api) return;
    const [anio, mes] = nuevoMes.split("-").map(Number);
    api.gotoDate(new Date(anio, mes - 1, 1));
  }
);

// ── Exponer updateSize para cuando el sidebar se colapsa ──────
function updateSize() {
  calRef.value?.getApi()?.updateSize();
}

defineExpose({ updateSize });
</script>

<style scoped>
.calendario-wrapper {
  background-color: var(--d-cream);
  border-radius: 14px;
  overflow: hidden;
  padding: 0.4rem;
}

/* ── Overrides FullCalendar con paleta Divisan ── */
:deep(.fc-daygrid-day-number) {
  color: var(--d-brown);
  font-weight: 700;
  font-size: 0.75rem;
  padding: 3px 5px;
}

:deep(.fc-col-header-cell-cushion) {
  color: var(--d-brown);
  font-weight: 800;
  font-size: 0.72rem;
  text-transform: uppercase;
  padding: 4px;
}

:deep(.fc-daygrid-day.fc-day-today) {
  background-color: rgba(96, 108, 56, 0.12) !important;
}

/* Días del mes actual → cursor pointer al hover */
:deep(.fc-daygrid-day:not(.fc-day-other):hover) {
  background-color: rgba(96, 108, 56, 0.06);
  cursor: pointer;
}

/* Días de meses adyacentes → no clickeables visualmente */
:deep(.fc-daygrid-day.fc-day-other) {
  cursor: default;
  pointer-events: none; /* bloquea clicks directamente en el DOM */
}
:deep(.fc-daygrid-day.fc-day-other .fc-daygrid-day-number) {
  opacity: 0.25;
}

:deep(.fc-scrollgrid) {
  border-color: rgba(96, 108, 56, 0.2) !important;
}

:deep(.fc-scrollgrid-sync-table td),
:deep(.fc-scrollgrid-sync-table th) {
  border-color: rgba(96, 108, 56, 0.15) !important;
}

:deep(.fc-daygrid-day-frame) {
  min-height: 0;
}

:deep(.fc-event) {
  margin: 1px 2px;
}
</style>
